import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Key,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  Zap,
  Activity,
  MessageCircle,
  Image,
  LayoutGrid,
  Server,
  Layers,
  Smartphone,
  DollarSign,
  Wand2,
  BarChart3,
  Users,
  Plus,
  Sparkles,
  Sliders,
  Coins,
  ShoppingCart,
  MessageSquareText,
  Shield,
  Menu,
  X,
  Globe,
  Search,
  HeartPulse,
} from 'lucide-react';
import { auth } from '../api/client';
import { cn } from '../lib/utils';
import ErrorBoundary from './ErrorBoundary';

type ServiceType = 'text' | 'image' | 'credits' | 'feedback';

const textNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'API Keys', href: '/keys', icon: Key },
  { name: 'Logs', href: '/logs', icon: FileText },
  { name: 'Conversations', href: '/conversations', icon: MessageSquare },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Devices', href: '/devices', icon: Smartphone },
  { name: 'Content Filter', href: '/blocklist', icon: Shield },
  { name: 'Diagnostics', href: '/diagnostics', icon: Activity },
  { name: 'App Health', href: '/app-health', icon: HeartPulse },
  { name: 'Search Backends', href: '/search/backends', icon: Globe },
  { name: 'Search Logs', href: '/search/logs', icon: Search },
  { name: 'Uploads', href: '/uploads', icon: Image },
];

const imageNavigation = [
  { name: 'Dashboard', href: '/image', icon: LayoutDashboard },
  { name: 'API Keys', href: '/image/keys', icon: Key },
  { name: 'Backends', href: '/image/backends', icon: Server },
  { name: 'Workflows', href: '/image/workflows', icon: Layers },
  { name: 'Transforms', href: '/image/transforms', icon: Wand2 },
  { name: 'Logs', href: '/image/logs', icon: FileText },
  { name: 'Gallery', href: '/image/gallery', icon: LayoutGrid },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Cost Analysis', href: '/cost-analysis', icon: DollarSign },
];

const creditsNavigation = [
  { name: 'Overview', href: '/credits', icon: BarChart3 },
  { name: 'Users', href: '/credits/users', icon: Users },
  { name: 'Purchases', href: '/credits/purchases', icon: ShoppingCart },
  { name: 'Grant', href: '/credits/grant', icon: Plus },
  { name: 'Generations', href: '/credits/generations', icon: Sparkles },
  { name: 'Config', href: '/credits/config', icon: Sliders },
];

const feedbackNavigation = [
  { name: 'Feedback', href: '/feedback', icon: MessageSquareText },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine service type from URL
  const getServiceFromPath = (path: string): ServiceType => {
    if (path.startsWith('/image')) return 'image';
    if (path.startsWith('/cost-analysis')) return 'image';
    if (path.startsWith('/credits')) return 'credits';
    if (path.startsWith('/feedback')) return 'feedback';
    return 'text';
  };
  const [serviceType, setServiceType] = useState<ServiceType>(getServiceFromPath(location.pathname));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Shared pages live in more than one service's nav (e.g. Settings is in both
    // Text and Image) — navigating to them keeps the current sidebar instead of
    // flipping the service, so you don't lose your place.
    const sharedPaths = ['/settings'];
    if (!sharedPaths.some(p => location.pathname.startsWith(p))) {
      setServiceType(getServiceFromPath(location.pathname));
    }
    setMobileMenuOpen(false); // Close mobile menu on navigation
  }, [location.pathname]);

  const navigation = serviceType === 'feedback'
    ? feedbackNavigation
    : serviceType === 'credits'
      ? creditsNavigation
      : serviceType === 'image'
        ? imageNavigation
        : textNavigation;

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // Ignore errors
    }
    navigate('/login');
  };

  const handleServiceChange = (type: ServiceType) => {
    setServiceType(type);
    if (type === 'image') {
      navigate('/image');
    } else if (type === 'credits') {
      navigate('/credits');
    } else if (type === 'feedback') {
      navigate('/feedback');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-50 md:hidden h-14 bg-[hsl(222,47%,7%)] border-b border-border/50 flex items-center px-4 gap-3">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="w-10 h-10 rounded-xl bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-glow">
            <Zap className="w-4 h-4 text-[hsl(222,47%,6%)]" />
          </div>
          <h1 className="text-sm font-bold text-gradient">Neural Command</h1>
        </div>
      </div>

      {/* Mobile overlay backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 w-72 bg-[hsl(222,47%,7%)] border-r border-border/50 flex flex-col',
        'transition-transform duration-300 ease-in-out',
        'md:translate-x-0',
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Decorative glow */}
        <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-cyan-500/5 to-transparent pointer-events-none" />

        {/* Logo */}
        <div className="relative h-20 flex items-center px-6 border-b border-border/50">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-glow">
              <Zap className="w-5 h-5 text-[hsl(222,47%,6%)]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gradient">Neural Command</h1>
              <p className="text-[10px] text-muted-foreground font-mono tracking-wider">AI PROXY CONTROL</p>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="md:hidden w-9 h-9 rounded-lg bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Service Type Tabs */}
        <div className="px-4 py-3">
          <div className="flex rounded-xl bg-secondary/50 p-1">
            <button
              onClick={() => handleServiceChange('text')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                serviceType === 'text'
                  ? 'bg-gradient-to-r from-cyan-500/20 to-violet-500/20 text-cyan-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <MessageCircle className="w-4 h-4" />
              <span>Text</span>
            </button>
            <button
              onClick={() => handleServiceChange('image')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                serviceType === 'image'
                  ? 'bg-gradient-to-r from-violet-500/20 to-amber-500/20 text-violet-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Image className="w-4 h-4" />
              <span>Image</span>
            </button>
            <button
              onClick={() => handleServiceChange('credits')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                serviceType === 'credits'
                  ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Coins className="w-4 h-4" />
              <span>Credits</span>
            </button>
            <button
              onClick={() => handleServiceChange('feedback')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                serviceType === 'feedback'
                  ? 'bg-gradient-to-r from-amber-500/20 to-rose-500/20 text-amber-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <MessageSquareText className="w-4 h-4" />
              <span>Feedback</span>
            </button>
          </div>
        </div>

        {/* Status indicator */}
        <div className="px-4 py-3 mx-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-emerald-400">System Online</span>
            <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500 pulse-live" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          <p className="px-3 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest mb-3">
            {serviceType === 'feedback' ? 'User Feedback' : serviceType === 'credits' ? 'Credit System' : serviceType === 'image' ? 'Image Generation' : 'Text Generation'}
          </p>
          {navigation.map((item, index) => {
            const isActive =
              item.href === '/' || item.href === '/image' || item.href === '/credits' || item.href === '/feedback'
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200',
                  'animate-fade-in-up',
                  isActive
                    ? 'nav-item-active'
                    : 'nav-item'
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center mr-3 transition-all duration-200',
                    isActive
                      ? serviceType === 'feedback'
                        ? 'bg-amber-500/20'
                        : serviceType === 'credits'
                          ? 'bg-emerald-500/20'
                          : serviceType === 'image'
                            ? 'bg-violet-500/20'
                            : 'bg-cyan-500/20'
                      : 'bg-secondary group-hover:bg-secondary/80'
                  )}
                >
                  <item.icon
                    className={cn(
                      'w-4 h-4 transition-colors',
                      isActive
                        ? serviceType === 'feedback'
                          ? 'text-amber-400'
                          : serviceType === 'credits'
                            ? 'text-emerald-400'
                            : serviceType === 'image'
                              ? 'text-violet-400'
                              : 'text-cyan-400'
                        : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                </div>
                <span>{item.name}</span>
                {isActive && (
                  <span className={cn(
                    'ml-auto w-1.5 h-1.5 rounded-full',
                    serviceType === 'feedback' ? 'bg-amber-400' : serviceType === 'credits' ? 'bg-emerald-400' : serviceType === 'image' ? 'bg-violet-400' : 'bg-cyan-400'
                  )} />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-border/50">
          <button
            onClick={handleLogout}
            className="group flex items-center w-full px-3 py-2.5 text-sm font-medium text-muted-foreground rounded-xl hover:bg-rose-500/10 hover:text-rose-400 transition-all duration-200"
          >
            <div className="w-9 h-9 rounded-lg bg-secondary group-hover:bg-rose-500/20 flex items-center justify-center mr-3 transition-all duration-200">
              <LogOut className="w-4 h-4 group-hover:text-rose-400 transition-colors" />
            </div>
            Logout
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/30">
          <p className="text-[10px] text-muted-foreground/50 font-mono">
            v1.0.0 • Neural Interface
          </p>
        </div>
      </aside>

      {/* Main content */}
      <div className="md:pl-72">
        {/* Top gradient bar */}
        <div className={cn(
          'h-1 mt-14 md:mt-0',
          serviceType === 'feedback'
            ? 'bg-gradient-to-r from-amber-500/50 via-rose-500/50 to-violet-500/50'
            : serviceType === 'credits'
              ? 'bg-gradient-to-r from-emerald-500/50 via-teal-500/50 to-cyan-500/50'
              : serviceType === 'image'
                ? 'bg-gradient-to-r from-violet-500/50 via-amber-500/50 to-rose-500/50'
                : 'bg-gradient-to-r from-cyan-500/50 via-violet-500/50 to-amber-500/50'
        )} />

        <main className="p-4 md:p-8 min-h-[calc(100vh-4px)]">
          {/* Per-page error boundary keyed on route — a render crash on one
              page (e.g. unexpected payload shape) shouldn't blank the
              entire admin app, and navigating away should reset the
              boundary so the new page renders fresh. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
